import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import type { CSSProperties, StyledProps, StyleProps } from '@front10';

import { FareMarkerData } from './classes/MarkerData';
import { MarkerData } from './classes/MarkerData';
import { getSelectedOrigin, visibleDestinationsSelector } from './hooks/useIMCState';

//-------------------------------------
// Atoms
//-------------------------------------
import Loading from '../../others/Loading';
import RadioSelect from '../../elements/RadioSelect';
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import Text, { TextProps } from '../../elements/Text/Text';
import FareItemMediumSize from '../../items/FareItemMediumSize';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import CardControlCheck from './atoms/CardControl';
import VisuallyHidden from '@reach/visually-hidden';

//-------------------------------------
// HOOKS
//-------------------------------------
import { usePlural } from '@front10/utils';
import useFareItemProps from './filters/useFareItemProps';
import useFares from './filters/useFares';
import { mapFilter } from './filters/useFares';
import useFilters from './filters/useFilters';
import { UseFaresAPIProps } from './filters/useFares';
import { notifyExitFullMode } from '../_commons/navigation';
import { Option } from './../../elements/RadioSelect/RadioSelect';

import useInteractiveMap, {
  isFullScreenSelector,
  toggleFullScreenSelector,
  toggleTopOffersSelector,
  setPreDestinationsSelector,
  getShowNoResult,
  useStore,
  setShowNoResult,
  setDestinationsSelectorOnlyRead
} from './hooks/useIMCState';
import { IMCFare } from './types';
import VectorMap, { MapActions, VectorMapProps } from './VectorMap';
import useCountriesData from './hooks/useCountriesData';

const regions = {
  africa: [8.150309, 20.326894],
  asia: [34.76405, 103.364055],
  europe: [45.454669, 4.30471],
  northAmerica: [39.234931, -98.304893],
  southAmerica: [-10.839898, -59.079845]
};

interface InteractiveMapProps {
  alertNoResults?: StyledProps<$TSFixMe>;
  alertError?: StyledProps<$TSFixMe>;
  alertLoading?: StyledProps<$TSFixMe>;
  loading?: StyledProps<$TSFixMe>;
  loadingBigger?: StyledProps<$TSFixMe>;
  fareList?: StyledProps<$TSFixMe>;
  popularDestinationsContainerSettings?: StyleProps;
  popularDestinationsLabelSettings?: TextProps;
  popularDestinationsValueSettings?: TextProps;
  showFareHeaderIataCode?: boolean;
  listContainerSettings?: StyledProps<$TSFixMe>;
  mapContainerSettings?: StyledProps<$TSFixMe>;
  vectorMapSettings?: Partial<VectorMapProps>;
  filters: ReturnType<typeof useFilters>;
  fares: ReturnType<typeof useFares>;
  fareItemOnClick: $TSFixMe;
  geoData: ReturnType<typeof useCountriesData>;
  regionCenter?: 'africa' | 'asia' | 'europe' | 'northAmerica' | 'southAmerica' | [number, number];
  selectOriginTextSettings: TextProps;
  showLoading?: boolean;
  isMobile?: boolean;
  forceCloseTooltips?: boolean;
  height?: number | string;
  lineLoading?: StyledProps<$TSFixMe>;
  dataForMarkerAction?: UseFaresAPIProps;
  timeToShowAdvancedLoading?: number;
  renderFilters?: StyledProps<$TSFixMe>;
  renderSecondaryFilters?: StyledProps<$TSFixMe>;
  filtersContainer?: StyledProps<$TSFixMe>;
  alertInfo?: StyledProps<$TSFixMe>;
  alertInfoTextSettings?: StyledProps<$TSFixMe>;
  sideBarContent?: StyledProps<$TSFixMe>;
  loadingCol?: StyledProps<$TSFixMe>;
  tabViewSettings?: StyledProps<$TSFixMe>;
  modal?: StyledProps<$TSFixMe>;
  modalHeader?: StyledProps<$TSFixMe>;
  modalButton?: StyledProps<$TSFixMe>;
  iconButtonBack?: StyledProps<$TSFixMe>;
  headerText?: StyledProps<$TSFixMe>;
  modalContent?: StyledProps<$TSFixMe>;
  mapContainerFullScreenSettings?: StyledProps<$TSFixMe>;
  toggleHiddenBtn?: StyledProps<$TSFixMe>;
  toggleHiddenIcon?: StyledProps<$TSFixMe>;
  toggleContainer?: StyledProps<$TSFixMe>;
  namesData?: $TSFixMe;
  showTitleFullScreen?: boolean;
  a11yInfoScreenReaderUsers?: boolean;
  visibleDestinations: string[] | null;
  activeTab?: 'Map' | 'List';
  enableMapMinHeightBehavior?: boolean;
  minHeightBehaviorSettings?: {
    heightBreakpoints: Array<number>;
    minHeights: Array<number>;
    windowDimensions: Array<{ width: number; height: number }>;
  };
  customSettingsForControls?: {
    enableCustomSettingsForControls: boolean;
    windowDimensions: Array<{ width: number; height: number }>;
    controlsSettings: Array<CSSProperties>;
  };
}

const Card = React.memo(FareItemMediumSize, (prevProps, nextProps) => {
  return (
    prevProps.focusElement === nextProps.focusElement &&
    prevProps.showCallToAction === nextProps.showCallToAction
  );
});

function InteractiveMap(props: InteractiveMapProps) {
  const {
    isMobile = false,
    geoData,
    namesData,
    loading = {},
    loadingBigger = {},
    loadingCol = {},
    regionCenter = 'northAmerica',
    vectorMapSettings = {},
    popularDestinationsContainerSettings = {},
    popularDestinationsLabelSettings = {},
    popularDestinationsValueSettings = {},
    listContainerSettings = {
      md: '4'
    },
    mapContainerSettings,
    fareList,
    fareList: { itemSettings },
    filters,
    fares,
    fareItemOnClick,
    alertError = {},
    alertLoading = {},
    alertNoResults = {},
    lineLoading = {
      bgColor: 'primary'
    },
    forceCloseTooltips = false,
    timeToShowAdvancedLoading = 3,
    renderFilters,
    renderSecondaryFilters,
    filtersContainer = {},
    alertInfo = {},
    alertInfoTextSettings = {},
    sideBarContent = {},
    tabViewSettings = {},
    modal = {},
    modalHeader = {},
    modalButton = {},
    iconButtonBack = {},
    modalContent = {},
    headerText = {},
    toggleHiddenBtn = {},
    toggleContainer = {},
    toggleHiddenIcon = {},
    mapContainerFullScreenSettings = {},
    showTitleFullScreen = true,
    a11yInfoScreenReaderUsers = false,
    visibleDestinations = [],
    activeTab = 'Map',
    enableMapMinHeightBehavior = false,
    minHeightBehaviorSettings = {
      heightBreakpoints: [],
      minHeights: [],
      windowDimensions: []
    },
    customSettingsForControls = {
      enableCustomSettingsForControls: false,
      windowDimensions: [],
      controlsSettings: []
    }
  } = props;
  const { translate } = useContext(Context);
  const pluralize = usePlural();

  const visibleDestinationsSelectorFn = useStore(visibleDestinationsSelector);
  const [prevVisibleDestinations, setPrevVisibleDestinations] = useState<
    typeof visibleDestinations
  >([]);

  if (visibleDestinations !== prevVisibleDestinations) {
    setPrevVisibleDestinations(visibleDestinations);
  }

  const vectorMapRef = useRef<MapActions>(null);
  const modalRef = React.useRef<$TSFixMe>(null);
  const modalHeight = useRef(null);
  const [focusOn, setFocusOn] = useState();
  const [toggleActive, setToggleButton] = useState(false);
  /** This logic is to control the loading that is displayed */
  const timer = useRef(0);
  const [timerStart, setTimerStart] = useState(0);
  const [timerTime, setTimerTime] = useState(0);

  const [showEndMarker, setShowEndMarker] = useState(true);
  const [filtersChanged, setFiltersChanged] = useState(filters.state);
  const [oldEndRouteMarker, setNewRouteMarker] = useState<MarkerData | undefined | null>(undefined);
  const { dispatch: filtersDispatch } = filters;
  const mapCenterSave = useMemo(() => {
    if (typeof regionCenter === 'string') {
      return regions[regionCenter] as [number, number];
    }
    return regionCenter as [number, number];
  }, [regionCenter]);
  const [currentCenter, setCurrentCenter] = useState(mapCenterSave);
  const newShowInitialData =
    props.vectorMapSettings?.showInitialData === undefined ||
    props.vectorMapSettings?.showInitialData === null
      ? true
      : props.vectorMapSettings?.showInitialData;
  const centerOriginLogic =
    props.vectorMapSettings?.centerOriginLogic === undefined ||
    props.vectorMapSettings?.centerOriginLogic === null
      ? true
      : props.vectorMapSettings?.centerOriginLogic;
  const {
    markers,
    destinationMapReadyOnly,
    setSelectedMarker,
    isOriginSelected,
    selectedOriginMarker,
    route,
    selectedDestinationMarker,
    isDestinationSelected,
    currentFares,
    setIsClearDestination,
    isClearDestination
  } = useInteractiveMap({
    filters,
    fares,
    mapRef: vectorMapRef,
    showInitialData: newShowInitialData,
    centerOriginLogic
  });

  const optionsTabs = React.useMemo(() => {
    return [
      {
        value: 'map-view',
        name: translate('cmp-interactive-map-map-view', 'Map View'),
        isClickable: isOriginSelected
      },
      {
        value: 'list-view',
        name: translate('cmp-interactive-map-list-view', 'List View'),
        isClickable: isOriginSelected
      }
    ];
  }, [isOriginSelected, translate]);

  const [tabActive, setTabActive] = useState<Option>(() =>
    activeTab === 'Map' ? optionsTabs[0] : optionsTabs[1]
  );

  const fetchFares = React.useCallback(
    (destination: [string]) => {
      if (props.dataForMarkerAction !== undefined) {
        const {
          getFares,
          prePopulation,
          integrationSettings,
          faresTransformationLogic,
          meta,
          pageContext,
          paginationSettings,
          filters,
          cache
        } = props.dataForMarkerAction;

        const filtersMapped = mapFilter({
          prePopulation: prePopulation,
          sorting: filters.sorting,
          paginationIndex: paginationSettings?.initialVisibleItems,
          faresTransformationLogic: faresTransformationLogic,
          origins: filters.origins,
          destinations: destination,
          budget: filters.budget,
          travelClasses: filters.travelClasses,
          flightType: filters.flightType,
          redemptionUnit: filters.redemptionUnit,
          currency: filters.currency,
          dateRange: {
            start: filters.dateRange?.start,
            end: filters.dateRange?.end,
            availableDays: filters.dateRange?.availableDays,
            checkedFlexibleDates: filters.dateRange?.checkedFlexibleDates,
            flexibleDates: filters.dateRange?.flexibleDates
          },
          flightStopCount: filters.flightStopCount?.value,
          isPrePopulation: false
        });

        return getFares(
          filtersMapped,
          integrationSettings,
          faresTransformationLogic,
          meta,
          pageContext,
          cache
        );
      }
      return undefined;
    },
    [props.dataForMarkerAction]
  );

  const { direction, theme } = useContext(Context);
  const [classNameMapContainerSettings, style] = useStyles(mapContainerSettings, {
    direction,
    theme
  });
  const [markerHover, setMarkerHover] = useState<FareMarkerData | MarkerData | undefined>(
    undefined
  );
  const fareMarkers = useMemo(() => markers.filter((m: MarkerData) => m.markerType === 'fare'), [
    markers
  ]);

  const handleCloseCardMap = () => {
    setFocusOn(undefined);
  };

  const toggleTopOffers = useStore(toggleTopOffersSelector);
  const showNoResult = useStore(getShowNoResult);
  const setShowNoResultFn = useStore(setShowNoResult);
  const setPreDestination = useStore(setPreDestinationsSelector);
  const setDestinationsSelectorOnlyReadDataFn = useStore(setDestinationsSelectorOnlyRead);
  const isFullScreen = useStore(isFullScreenSelector);
  const toggleFullScreen = useStore(toggleFullScreenSelector);

  // TODO: What does this function do? center map when origin is removed?
  useEffect(() => {
    if (!isOriginSelected) {
      vectorMapRef.current?.resetView(mapCenterSave);
      setNewRouteMarker(undefined);
      setFocusOn(undefined);
      setMarkerHover(undefined);
      setPreDestination([]);
      if (forceCloseTooltips) {
        vectorMapRef.current?.cleanTooltips();
      }
      if (isMobile && !isFullScreen) {
        setTabActive(optionsTabs[0]);
        setCurrentCenter(mapCenterSave);
      }
    } else {
      setDestinationsSelectorOnlyReadDataFn([]);
      setTabActive(() => (activeTab === 'Map' ? optionsTabs[0] : optionsTabs[1]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceCloseTooltips, isOriginSelected, vectorMapRef]);

  useEffect(() => {
    //INFO: Close oldNewRouteMarker when user produce a new search or changed any filters
    if (JSON.stringify(filtersChanged) !== JSON.stringify(filters.state)) {
      setFiltersChanged(filters.state);
      setNewRouteMarker(undefined);
      setFocusOn(undefined);
      a11yMessagesMap.current = {
        loading: false,
        no_fares_found: false,
        number_of_results: false
      };
    }
  }, [filters.state, filtersChanged]);

  // const userInteraction = useStore(userInteractionSelector);

  //INFO: It was necessary to add this logic at a higher level to
  //      avoid reRender within the VectorMap and in the cardControl
  useEffect(() => {
    if ('checkTopOffers' in vectorMapSettings?.cardControlBtnSettings) {
      toggleTopOffers(vectorMapSettings?.cardControlBtnSettings?.checkTopOffers);
    }
  }, [toggleTopOffers, vectorMapSettings?.cardControlBtnSettings]);

  useEffect(() => {
    const rect = modalRef.current?.getBoundingClientRect();
    if (rect && isFullScreen) {
      modalHeight.current = rect.height;
    }
  }, [modalRef, isFullScreen]);

  useEffect(() => {
    /**
     * Resetting the toggleButton to the initial state
     */
    setToggleButton(false);

    if (isFullScreen) {
      toggleFullScreenButtonRef?.current?.focus();
    }
  }, [isFullScreen]);

  useEffect(() => {
    //TODO: when moving everything to a global state the useEffect is used
    //      to fire the event when the value changes, it is passing that the
    //      value was firing when the dispatch changes, this condition is
    //      added so that it is fired only under certain conditions.
    if (isFullScreen || isFullScreen === false) {
      filtersDispatch({ type: 'EXPAND_MAP', payload: { value: isFullScreen } });
      vectorMapRef.current?.updateSize();
    }
  }, [filtersDispatch, isFullScreen]);

  const selectedOrigin: any = useStore(getSelectedOrigin);
  useEffect(() => {
    if (isOriginSelected && !centerOriginLogic) {
      vectorMapRef.current?.setView(mapCenterSave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrigin]);

  useEffect(() => {
    if (isFullScreen && selectedOriginMarker) {
      if (centerOriginLogic) {
        vectorMapRef.current?.setView(selectedOriginMarker.location);
      } else {
        if (oldEndRouteMarker) {
          vectorMapRef.current?.setView(oldEndRouteMarker.location);
        } else {
          vectorMapRef.current?.setView(currentCenter);
        }
      }
    } else if (!isFullScreen && oldEndRouteMarker) {
      vectorMapRef.current?.moveTo(oldEndRouteMarker.location);
      setTimeout(() => vectorMapRef.current?.updateVisibleMarkers(), 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen, centerOriginLogic]);

  const handleZoomEnd = useCallback(
    (oldZoom: number, newZoom: number) => {
      filtersDispatch({ type: 'ZOOM', payload: { expanded: isFullScreen, oldZoom, newZoom } });
    },
    [filtersDispatch, isFullScreen]
  );

  const handleDragEnd = useCallback(() => {
    filtersDispatch({ type: 'MORE_DEALS' });
  }, [filtersDispatch]);

  const fareItemSettings = useFareItemProps(itemSettings, filtersDispatch, fareItemOnClick);

  const endRouteSettings = vectorMapSettings.endRouteSettings || {};

  const [queryInProgress, setQueryInProgress] = useState(false);

  let fareItemCardSettings = useFareItemProps(
    endRouteSettings.fareItemCardSettings || {},
    filtersDispatch,
    fareItemOnClick
  );
  endRouteSettings.fareItemCardSettings = fareItemCardSettings;

  if (!endRouteSettings.checkAvailabilityCardSettings) {
    endRouteSettings.checkAvailabilityCardSettings = {};
  }
  if (!endRouteSettings.checkAvailabilityCardSettings.fareItemSettings) {
    endRouteSettings.checkAvailabilityCardSettings.fareItemSettings = {};
  }

  let filtersState = filters.state;
  if (isDestinationSelected && selectedDestinationMarker) {
    filtersState.destination = { value: selectedDestinationMarker.value };
  }
  if (isClearDestination) {
    filtersState.destination = { value: '' };
  }
  const checkAvailabilityCardSettings = useFareItemProps(
    endRouteSettings.checkAvailabilityCardSettings.fareItemSettings,
    filtersDispatch,
    fareItemOnClick,
    filtersState
  );

  endRouteSettings.checkAvailabilityCardSettings.fareItemSettings = checkAvailabilityCardSettings;

  const isError = fares.isError || filters.isError || geoData.isError;
  const refetch = () => {
    if (fares.isError) {
      fares.refetch();
    }
    if (filters.isError) {
      filters.refetch();
    }
    if (geoData.isError) {
      geoData.refetch();
    }
  };

  const loadingFares =
    fares.isFetching ||
    filters.locations.destination.isLoading ||
    queryInProgress ||
    namesData.isLoading;

  const loadingTime = Math.floor(timerTime / 1000) - Math.floor(timerStart / 1000);

  useEffect(() => {
    if (loadingFares) {
      setTimerStart(Date.now() - timerTime);
      setTimerTime(timerTime);
      timer.current = window.setInterval(() => {
        setTimerTime(Date.now() - timerStart);
      }, 1000);
    } else {
      setTimerStart(0);
      setTimerTime(0);
      clearInterval(timer.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFares]);

  const cardFocusRef = useRef<any>(null);
  const handleMarkerClick = async (marker: MarkerData, sendTrackingEvent: boolean = true) => {
    const makeMoveAction = props.vectorMapSettings?.enableCenterCard === false;
    setMarkerHover(undefined);
    if (marker.markerType === 'fare') {
      setFocusOn(marker.fare.index);
      setTimeout(() => setSelectedMarker(marker, sendTrackingEvent, makeMoveAction));
      setShowEndMarker(true);
      setNewRouteMarker(marker);
    } else if (marker.markerType === 'noFare') {
      setQueryInProgress(true);
      const res = await fetchFares([marker.value]);
      setQueryInProgress(false);
      if (res !== undefined && res.length !== 0) {
        const data = res[0];
        const newMarker: MarkerData = { ...marker, markerType: 'fare', fare: data };
        setNewRouteMarker(newMarker);
        setTimeout(() => setSelectedMarker(newMarker, sendTrackingEvent, makeMoveAction));
        setShowEndMarker(true);
        setFocusOn(undefined);
      } else {
        setTimeout(() => setSelectedMarker(marker, sendTrackingEvent, makeMoveAction));
        setShowEndMarker(true);
        setNewRouteMarker(marker);
        setFocusOn(undefined);
      }
    } else {
      //INFO: Selecting the point of origin when clicking on the map without prepopular
      setTimeout(() => setSelectedMarker(marker, sendTrackingEvent));
      setNewRouteMarker(marker);
    }
  };

  const handleFareClick = (fare: IMCFare, makeAction: boolean = true) => {
    for (const marker of fareMarkers) {
      if (marker.value === fare.destination) {
        handleMarkerClick(marker, false);
        setShowEndMarker(true);
        return;
      }
    }
  };

  const showFareHeaderIataCode =
    props?.showFareHeaderIataCode === null || props?.showFareHeaderIataCode === undefined
      ? true
      : props?.showFareHeaderIataCode;

  const renderFaresHeader = (
    <Box
      marginBottom="20px"
      display="flex"
      style={{ flexWrap: 'wrap' }}
      {...popularDestinationsContainerSettings}
    >
      <Text type="span" marginRight="1" {...popularDestinationsLabelSettings}>
        {translate(
          `cmp-interactive-map-container-popular-destinations-from`,
          'Popular destinations from'
        )}
      </Text>
      {isOriginSelected && (
        <Box className="fr-popular-destinations-value" display="flex" style={{ flexWrap: 'wrap' }}>
          <Text {...popularDestinationsValueSettings} type="span">
            {selectedOriginMarker.name}
          </Text>
          {showFareHeaderIataCode === true && (
            <Text type="span" marginLeft="1" {...popularDestinationsValueSettings}>
              {'(' + selectedOriginMarker.value + ')'}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );

  const [hiddenMessage, setHiddenMessage] = React.useState(false);
  React.useEffect(() => {
    if (!fares.isError && !fares.isFetching && fares.data) {
      setHiddenMessage(true);
    }
    const timeout = setTimeout(() => setHiddenMessage(false), 4000);

    return () => clearTimeout(timeout);
  }, [fares.data, fares.isFetching, fares.isError]);

  const a11yMessagesMap = React.useRef<{
    number_of_results?: boolean;
    no_fares_found?: boolean;
    loading?: boolean;
  }>({ loading: false, no_fares_found: false, number_of_results: false });

  const renderAccessibilityMessagesFares = (
    <VisuallyHidden role="status" aria-live="polite" aria-atomic="true" id="number-of-results">
      {a11yMessagesMap?.current?.number_of_results &&
        hiddenMessage &&
        fares?.data &&
        pluralize({
          key: 'cmp-interactive-map-elements-founds',
          defaultValue: 'Found {count} results',
          count: fares.data.length
        })}
    </VisuallyHidden>
  );

  const renderFaresList = !fares.isError &&
    !fares.isFetching &&
    !showNoResult &&
    fares.data?.length !== 0 &&
    currentFares.length !== 0 &&
    visibleDestinationsSelectorFn &&
    visibleDestinationsSelectorFn.length !== 0 && (
      <FareListTwoColumns
        {...fareList}
        itemSettings={fareItemSettings}
        ItemComponentInstance={Card}
        itemsData={currentFares}
        onItemActive={handleFareClick}
        focusOn={focusOn}
        focusReferences={(ref: any) => {
          cardFocusRef.current = ref;
        }}
      />
    );

  const renderRadioSelect = (
    <RadioSelect
      {...tabViewSettings}
      selectedOption={tabActive}
      options={optionsTabs}
      onChange={(e) => {
        if (vectorMapRef?.current?.getCurrentCenter() !== undefined) {
          setCurrentCenter(vectorMapRef?.current?.getCurrentCenter());
        }
        setTabActive(e);
      }}
    />
  );

  const renderAlertInfo = !isOriginSelected && !isError && (
    <Box {...alertInfo}>
      <Text {...alertInfoTextSettings}>
        {translate(
          'cmp-interactive-map-alert-info',
          'Select departure city to see available route destinations'
        )}
      </Text>
    </Box>
  );

  const renderErrorAlert = isError && <ContainerErrorAlert {...alertError} tryAgain={refetch} />;
  useEffect(() => {
    let newMap = { ...a11yMessagesMap?.current };
    if (
      !a11yMessagesMap?.current?.loading &&
      !fares.isError &&
      loadingFares &&
      fares?.status === 'loading'
    ) {
      newMap.loading = true;
    }
    if (
      !a11yMessagesMap?.current?.no_fares_found &&
      !fares.isError &&
      !loadingFares &&
      (fares.data === undefined || fares.data?.length === 0)
    ) {
      newMap.no_fares_found = true;
    }
    if (
      !a11yMessagesMap?.current?.number_of_results &&
      !fares.isError &&
      !fares.isFetching &&
      fares.data
    ) {
      newMap.number_of_results = true;
    }
    a11yMessagesMap.current = { ...newMap };
  }, [fares, loadingFares]);

  const renderAlertLoadingA11y = (
    <>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true" id="no-fares-found">
        {a11yMessagesMap?.current?.no_fares_found && (
          <span
            dangerouslySetInnerHTML={{
              __html: translate(
                alertNoResults.labelKeyTitle ?? 'cmp-no-featured-popular-fares-alert-info',
                'No fares were found for the selected criteria. Please try again with different City, Date Range and Budget.'
              )
            }}
          />
        )}
      </VisuallyHidden>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true" id="loading-fares">
        {a11yMessagesMap?.current?.loading &&
          translate('cmp-loading-fares', 'Content is loading...')}
      </VisuallyHidden>
    </>
  );

  useEffect(() => {
    if (!isOriginSelected) {
      setShowNoResultFn(true);
      setPreDestination([]);
    } else {
      setShowNoResultFn(false);
    }
    // FIXME: A better idea is probably to emulate getDerivedStateFromProps by setting the state
    // directly on render https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops,
    // therefore not needing to ignore the deps array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOriginSelected]);

  const renderAlertNoResult = isOriginSelected &&
    ((fares.status === 'success' &&
      !fares.isFetching &&
      !loadingFares &&
      (showNoResult || fares.data?.length === 0)) ||
      visibleDestinationsSelectorFn === null ||
      (showNoResult && visibleDestinationsSelectorFn.length === 0)) && (
      <NoFeaturedPopularFaresAlert {...alertNoResults} enableAccessibilityMessage={true} />
    );

  /**
   * In the EDS EM it does not specify an alert for loading, it is
   * added to improve compatibility with disabled people. We would be
   * showing the loading for the first 3 seconds of loading for its
   * visual counterpart of the loading line in the map, when it exceeds
   * 3s it hides since the other loading shows detailed information of
   * what is happening.
   */
  const renderAlertLoading = ((loadingFares &&
    !!renderFaresList === false &&
    !!renderAlertNoResult === false) ||
    (!!renderFaresList === false && !!renderAlertNoResult === false)) &&
    (visibleDestinationsSelectorFn !== null ||
      (visibleDestinationsSelectorFn !== null &&
        (visibleDestinationsSelectorFn as any).length !== 0)) && (
      <NoFeaturedPopularFaresAlert
        labelKeyTitle="cmp-loading-fares"
        {...alertLoading}
        enableAccessibilityMessage={true}
      />
    );

  const renderList = isOriginSelected && !isError && (
    <>
      {renderFaresHeader}
      {renderAlertLoading}
      {renderAccessibilityMessagesFares}
      {renderFaresList}
      {renderAlertNoResult}
    </>
  );

  const renderLoading = () => {
    if (loadingFares) {
      if (loadingTime > 0 && loadingTime <= timeToShowAdvancedLoading) {
        return (
          <Col
            className={`${isFullScreen ? 'fr-interactive-map-full-screen-loading' : ''} ${
              !isMobile && !isFullScreen
                ? 'fr-col-md-8 fr-col-sm-8 fr-offset-md-4 fr-offset-sm-4'
                : ''
            } fr-interactive-map-loading`}
            position="absolute"
            height="100"
            {...loadingCol}
          >
            <Box className="line-loading" {...lineLoading}></Box>
            <Loading {...loading} isLoading={false} />
          </Col>
        );
      }
      if (loadingTime > timeToShowAdvancedLoading) {
        return (
          <Col
            className="fr-col-12 fr-interactive-map-loading-full"
            position="absolute"
            top={isFullScreen ? '0' : null}
            height="100"
            {...loadingCol}
          >
            <Loading {...loadingBigger} isLoading={false} />
          </Col>
        );
      }
    }
    if (!isOriginSelected) {
      return (
        <Col
          className={`${isFullScreen ? 'fr-interactive-map-full-screen-loading' : ''} ${
            !isMobile && !isFullScreen
              ? 'fr-col-md-8 fr-col-sm-8 fr-offset-md-4 fr-offset-sm-4'
              : ''
          } fr-interactive-map-loading-without-origin`}
          position="absolute"
          height="100"
          {...loadingCol}
        >
          <Loading {...loading} isLoading={false} />
        </Col>
      );
    }
    return null;
  };

  const newStyles: CSSProperties = isFullScreen
    ? {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '-webkit-fill-available',
        padding: '0',
        margin: '0',
        zIndex: 21
      }
    : {
        ...style,
        zIndex:
          activeTab === 'List' && isMobile && tabActive.value === 'list-view' ? -1 : style.zIndex,
        position:
          activeTab === 'List' && isMobile && tabActive.value === 'list-view'
            ? 'absolute'
            : style.position
      };

  const toggleFullScreenButtonRef = useRef<any>(null);
  const mapContainerFullScreenRef = useRef<HTMLDivElement>(null);
  const [containerMapHeight, setContainerMapHeight] = useState<number | null>(null);

  useEffect(() => {
    if (enableMapMinHeightBehavior && !isFullScreen && !isMobile && containerMapHeight !== null) {
      setContainerMapHeight(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen]);

  useEffect(() => {
    if (
      enableMapMinHeightBehavior &&
      isFullScreen &&
      !isMobile &&
      mapContainerFullScreenRef.current &&
      containerMapHeight === null
    ) {
      const height = mapContainerFullScreenRef.current?.getBoundingClientRect()?.height;
      setContainerMapHeight(height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainerFullScreenRef.current]);

  const mapContainerFullScreenStyle: CSSProperties = useMemo(() => {
    if (enableMapMinHeightBehavior && isFullScreen && !isMobile && containerMapHeight !== null) {
      const { heightBreakpoints, minHeights, windowDimensions } = minHeightBehaviorSettings;
      for (let i = 0; i < windowDimensions.length; i++) {
        const { width, height } = windowDimensions[i];
        if (
          width === window.outerWidth &&
          height === window.outerHeight &&
          containerMapHeight <= heightBreakpoints[i]
        ) {
          return {
            minHeight: minHeights[i]
          };
        }
      }
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerMapHeight]);

  const minHeightStyles: CSSProperties = useMemo(() => {
    const baseStyle: CSSProperties = {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '-webkit-fill-available',
      padding: '0',
      margin: '0',
      zIndex: 21
    };
    if (enableMapMinHeightBehavior && isFullScreen && !isMobile && containerMapHeight !== null) {
      const { heightBreakpoints, windowDimensions } = minHeightBehaviorSettings;
      for (let i = 0; i < windowDimensions.length; i++) {
        const { width, height } = windowDimensions[i];
        if (
          width === window.outerWidth &&
          height === window.outerHeight &&
          containerMapHeight <= heightBreakpoints[i]
        ) {
          return {
            ...baseStyle,
            overflowY: 'auto'
          };
        }
      }
    }
    return {
      ...baseStyle
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerMapHeight]);

  const newMapContainerFullScreenSettings = useMemo(() => {
    if (enableMapMinHeightBehavior && isFullScreen && !isMobile && containerMapHeight !== null) {
      const {
        enableCustomSettingsForControls,
        windowDimensions,
        controlsSettings
      } = customSettingsForControls;
      if (enableCustomSettingsForControls) {
        for (let i = 0; i < windowDimensions.length; i++) {
          const { width, height } = windowDimensions[i];
          if (width === window.outerWidth && height === window.outerHeight) {
            return {
              ...mapContainerFullScreenSettings,
              'cmp-styles': {
                ...mapContainerFullScreenSettings['cmp-styles'],
                ...controlsSettings[i]
              }
            };
          }
        }
      }
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerMapHeight]);

  const renderMap = (
    <>
      {isFullScreen && (
        <Box position="relative" {...modal} focusReferences={modalRef}>
          <Box {...modalHeader}>
            <Button
              {...modalButton}
              ariaLabel={translate(
                'cmp-interactive-map-container-exit-full-screen-control-btn',
                'Exit full screen'
              )}
              onClick={() => {
                notifyExitFullMode();
                toggleFullScreen(!isFullScreen);
              }}
              focusReferences={(e: any) => {
                toggleFullScreenButtonRef.current = e;
              }}
            >
              <Icon {...iconButtonBack} icon="keyboard_arrow_up" />
            </Button>
            {showTitleFullScreen && (
              <Text {...headerText}>
                {translate('cmp-interactive-map-full-screen-title-header', 'Interactive map')}
              </Text>
            )}
          </Box>
          <Row {...modalContent}>
            {renderFilters}
            {isMobile && (
              <Button {...toggleHiddenBtn} onClick={() => setToggleButton(!toggleActive)}>
                {translate(
                  toggleActive
                    ? 'cmp-interactive-map-toggle-expanded'
                    : 'cmp-interactive-map-toggle-collapsed'
                )}
                <Icon
                  viewBox="0 0 1024 1024"
                  size={17}
                  icon={toggleActive ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  {...toggleHiddenIcon}
                />
              </Button>
            )}
            {(!isMobile || toggleActive) && (
              <Box {...toggleContainer}>
                {renderSecondaryFilters}
                <CardControlCheck {...(vectorMapSettings.cardControlBtnSettings || {})} />
              </Box>
            )}
          </Row>
        </Box>
      )}

      <Box
        focusReferences={mapContainerFullScreenRef}
        role={a11yInfoScreenReaderUsers ? 'region' : null}
        ariaLabel={
          a11yInfoScreenReaderUsers
            ? translate(
                'cmp-interactive-map-a11y-info-screen-reader-users',
                'The map view is mostly a help to sighted users. The screen reader users have all the fares information available in the destination list.'
              )
            : null
        }
        width="100"
        height="100"
        {...newMapContainerFullScreenSettings}
        style={
          enableMapMinHeightBehavior && !isMobile && isFullScreen ? mapContainerFullScreenStyle : {}
        }
      >
        {isFullScreen && loadingFares && <Row>{renderLoading()}</Row>}
        {isFullScreen && !isOriginSelected && newShowInitialData && (
          <Row>
            <Col
              className={`${
                isFullScreen ? 'fr-interactive-map-full-screen-loading' : ''
              } fr-interactive-map-loading-without-origin`}
              position="absolute"
              height="100"
              {...loadingCol}
            >
              <Loading {...loading} isLoading={false} />
            </Col>
          </Row>
        )}
        <VectorMap
          ref={vectorMapRef}
          {...vectorMapSettings}
          isOriginSelected={isOriginSelected}
          route={route}
          markers={markers}
          destinationMapReadyOnly={destinationMapReadyOnly}
          markerNames={namesData.data}
          fareMarkers={fareMarkers}
          initCenter={currentCenter}
          geoJSONData={geoData.data}
          markersSettings={{
            ...(vectorMapSettings.markersSettings || {}),
            onClick: (marker: MarkerData) => {
              handleMarkerClick(marker, true);
              setTimeout(() => cardFocusRef.current?.focus());
            },
            onFocus: (marker: MarkerData) => {
              handleMarkerClick(marker, false);
            }
          }}
          endRouteSettings={endRouteSettings}
          isMobile={isMobile}
          isLoading={loadingFares}
          onZoomEnd={handleZoomEnd}
          onDragEnd={handleDragEnd}
          showEndMarker={showEndMarker}
          setShowEndMarker={setShowEndMarker}
          oldEndRouteMarker={oldEndRouteMarker}
          setNewRouteMarker={setNewRouteMarker}
          handleCloseCardMap={handleCloseCardMap}
          markerHover={markerHover}
          setMarkerHover={setMarkerHover}
          setCurrentCenter={setCurrentCenter}
          setIsClearDestination={setIsClearDestination}
          filterState={filters.state}
        />
      </Box>
    </>
  );

  const renderContainerMap = (tabActive.value === 'map-view' ||
    (activeTab === 'List' && isMobile && tabActive.value === 'list-view')) && (
    <Col
      display="flex"
      justifyContent="center"
      paddingLeft="10px"
      flexDirection="column"
      cmp-styles={{ 'min-height': '520px' }}
      className={`${classNameMapContainerSettings} ${
        isFullScreen ? 'fr-map-container-expanded' : ''
      }`}
      style={enableMapMinHeightBehavior && !isMobile && isFullScreen ? minHeightStyles : newStyles}
    >
      {isFullScreen ? (
        <Box
          height="100"
          cmp-styles={{ '#fr-imc-fullscreen': { height: `calc(100% - ${modalHeight.current}px)` } }}
        >
          <Box id="fr-imc-fullscreen">
            {/* Momentarily disabling focusTrap */}
            {/* <FocusLock returnFocus group="fullScreen" lockProps={{ id: 'fr-imc-fullscreen' }}> */}
            {renderMap}
            {/* </FocusLock> */}
          </Box>
        </Box>
      ) : (
        renderMap
      )}
    </Col>
  );

  return (
    <>
      {!isFullScreen && renderLoading()}
      <Col md={4} sm={!isMobile ? 4 : null} paddingRight="10px" {...sideBarContent}>
        <Box {...filtersContainer}>
          {renderFilters}
          {renderSecondaryFilters}
        </Box>
        <Box
          style={{ height: props.height }}
          {...listContainerSettings}
          display="flex"
          flexDirection="column"
        >
          {renderAlertInfo}
          {renderErrorAlert}
          {!isMobile && renderList}
          {isMobile && renderRadioSelect}
        </Box>
      </Col>
      {renderContainerMap}
      {isMobile && tabActive.value === 'list-view' && renderList}
      {renderAlertLoadingA11y}
    </>
  );
}

InteractiveMap.displayName = 'InteractiveMap';

export default React.memo(InteractiveMap);
